/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { setToastMessage } from "../../store/slices/app/appSlice";
import InvoiceClientSelector from "../../components/invoices/InvoiceClientSelector";
import OcOrderSelector, {
  OcOrderSelectorItem,
} from "../../components/invoices/OcOrderSelector";
import InvoiceConfirmDialog from "../../components/invoices/InvoiceConfirmDialog";
import InvoiceItemsTable, {
  sumSubtotals,
} from "../../components/invoices/InvoiceItemsTable";
import {
  useGetInvoiceClientsQuery,
  useGetInvoiceIssuersQuery,
  useGetInvoiceOcOrdersQuery,
  useCreateInvoiceMutation,
} from "../../store/apis/invoiceApi";
import {
  CreateInvoiceRequest,
  InvoiceClient,
  InvoiceItem,
} from "../../types/invoice";
import { formatEUR, only2dec } from "../../utils/oc/ocCalc";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import PageContainer from "../../components/layout/PageContainer";

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyItem = (): InvoiceItem => ({
  concepto: "",
  subtitulo: "",
  subtotal: "",
});

// Estilo común para las etiquetas de campo (label encima del control).
const fieldLabelSx = { fontWeight: 600, mb: 0.5, display: "block" } as const;

// Marca interna de la línea de Fee Agencia, igual que FEE_LINE_FLAG en las OCs. Sirve para
// localizarla y reemplazarla al recalcular. El backend reconstruye cada línea a partir de
// concepto/subtitulo/subtotal, así que esta propiedad extra no viaja en el payload.
const FEE_LINE_FLAG = "__feeLine";

const isFeeLine = (it: InvoiceItem): boolean =>
  Boolean((it as unknown as Record<string, unknown>)[FEE_LINE_FLAG]);

const Invoices: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const collapsed = useSelector(selectSidebarCollapsed);

  const { data: clientsResp, error: clientsError } =
    useGetInvoiceClientsQuery();
  const { data: issuersResp, error: issuersError } =
    useGetInvoiceIssuersQuery();
  const [createInvoice, { isLoading: isSaving }] = useCreateInvoiceMutation();

  const clients = useMemo(() => clientsResp?.data ?? [], [clientsResp]);
  const issuers = useMemo(() => issuersResp?.data ?? [], [issuersResp]);

  // ---- Cabecera ----
  const [invoiceDate, setInvoiceDate] = useState(todayISO());
  const [concept, setConcept] = useState("");

  // ---- Emisora ----
  // Sin emisora predeterminada a propósito: hay que elegirla, para no acabar facturando
  // desde la primera de la lista sin darse cuenta. handleOpenConfirm avisa si falta.
  const [issuerId, setIssuerId] = useState<string>("");

  // ---- Cliente ----
  const [billingId, setBillingId] = useState<string | null>(null);
  const pickClient = (c: InvoiceClient) => setBillingId(c.billingId);

  // ---- Orden de Compra (obligatoria) ----
  const [ocOrderId, setOcOrderId] = useState<string | null>(null);
  const pickOcOrder = (o: OcOrderSelectorItem) => setOcOrderId(o.id);

  // ---- Líneas ----
  const [items, setItems] = useState<InvoiceItem[]>([emptyItem()]);
  const updateItem = (idx: number, patch: Partial<InvoiceItem>) =>
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, ...patch } : it))
    );
  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const deleteItem = (idx: number) =>
    setItems((prev) => prev.filter((_, i) => i !== idx));

  const total = useMemo(() => sumSubtotals(items), [items]);

  // Escribe la línea de Fee Agencia con el fee de la empresa. El variable es un porcentaje
  // sobre la inversión de las demás líneas; el fijo, el importe tal cual. Reemplaza la línea
  // de fee anterior, así que volver a pulsar recalcula sobre la inversión actual. La línea no
  // lleva subtítulo: el porcentaje no debe salir impreso en la factura.
  const applyFee = (kind: "variable" | "fixed") => {
    if (!selectedClient) return;
    const { feeVariable, feeFixed } = selectedClient;
    setItems((prev) => {
      const base = prev.filter((it) => !isFeeLine(it));
      const amount =
        kind === "variable"
          ? only2dec((sumSubtotals(base) * feeVariable) / 100)
          : only2dec(feeFixed);
      const feeItem = {
        concepto: t("invoices.feeLine.concepto"),
        subtitulo: "",
        subtotal: String(amount),
        [FEE_LINE_FLAG]: true,
      } as unknown as InvoiceItem;
      return [...base, feeItem];
    });
  };

  // Menú de elección, solo necesario cuando la empresa tiene los dos fees configurados.
  const [feeMenuAnchor, setFeeMenuAnchor] = useState<HTMLElement | null>(null);

  const handleAddFeeClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!selectedClient) {
      dispatch(
        setToastMessage({
          message: t("invoices.toasts.selectClient"),
          severity: "error",
        })
      );
      return;
    }
    const { feeVariable, feeFixed } = selectedClient;
    if (!feeVariable && !feeFixed) {
      dispatch(
        setToastMessage({
          message: t("invoices.toasts.noFeeConfigured"),
          severity: "error",
        })
      );
      return;
    }
    // Con los dos configurados no adivinamos cuál toca: que lo elija quien factura.
    if (feeVariable > 0 && feeFixed > 0) {
      setFeeMenuAnchor(event.currentTarget);
      return;
    }
    applyFee(feeVariable > 0 ? "variable" : "fixed");
  };

  const pickFee = (kind: "variable" | "fixed") => {
    applyFee(kind);
    setFeeMenuAnchor(null);
  };

  // ---- Confirmación antes de enviar a Holded ----
  const [confirmOpen, setConfirmOpen] = useState(false);

  const selectedIssuer = useMemo(
    () => issuers.find((i) => i.id === issuerId) || null,
    [issuers, issuerId]
  );
  const selectedClient = useMemo(
    () => clients.find((c) => c.billingId === billingId) || null,
    [clients, billingId]
  );

  // OCs del cliente ya elegido: el selector no debe ofrecer las de todo el sistema.
  // Hasta que hay cliente no se pide nada.
  const { data: ocOrdersResp, error: ocOrdersError } =
    useGetInvoiceOcOrdersQuery(selectedClient?.companyId ?? "", {
      skip: !selectedClient,
    });
  const ocOrders = useMemo(() => ocOrdersResp?.data ?? [], [ocOrdersResp]);
  const selectedOcOrder = useMemo(
    () => ocOrders.find((o) => o.id === ocOrderId) || null,
    [ocOrders, ocOrderId]
  );

  // Al cambiar de cliente cambia la lista de OCs: si la que estaba elegida ya no está,
  // se limpia para no enviar la OC de otro cliente.
  useEffect(() => {
    if (ocOrderId && !ocOrders.some((o) => o.id === ocOrderId)) {
      setOcOrderId(null);
    }
  }, [ocOrders, ocOrderId]);

  useEffect(() => {
    if (clientsError || issuersError || ocOrdersError) {
      dispatch(
        setToastMessage({
          message: t("invoices.toasts.loadError"),
          severity: "error",
        })
      );
    }
  }, [clientsError, issuersError, ocOrdersError, dispatch, t]);

  // Valida el formulario y, si todo es correcto, abre el modal de confirmación.
  const handleOpenConfirm = () => {
    if (!issuerId) {
      dispatch(
        setToastMessage({
          message: t("invoices.toasts.selectIssuer"),
          severity: "error",
        })
      );
      return;
    }
    if (!billingId) {
      dispatch(
        setToastMessage({
          message: t("invoices.toasts.selectClient"),
          severity: "error",
        })
      );
      return;
    }
    if (!ocOrderId) {
      dispatch(
        setToastMessage({
          message: t("invoices.toasts.selectOcOrder"),
          severity: "error",
        })
      );
      return;
    }
    if (items.every((it) => !it.concepto.trim())) {
      dispatch(
        setToastMessage({
          message: t("invoices.toasts.needsItems"),
          severity: "error",
        })
      );
      return;
    }
    setConfirmOpen(true);
  };

  // Ejecuta el POST tras confirmar en el modal.
  const handleConfirmSend = async () => {
    if (!issuerId || !billingId || !ocOrderId) return;

    const body: CreateInvoiceRequest = {
      invoiceDate,
      concept: concept.trim() || undefined,
      issuerCompanyId: issuerId,
      billingId,
      ocOrderId,
      items: items.map((it) => ({
        concepto: it.concepto,
        subtitulo: it.subtitulo,
        subtotal:
          typeof it.subtotal === "string"
            ? parseFloat(it.subtotal || "0") || 0
            : it.subtotal,
      })),
    };

    try {
      const resp = await createInvoice(body).unwrap();
      const detail = resp.data;
      // 201 — creada y enviada a Holded
      setConfirmOpen(false);
      dispatch(
        setToastMessage({
          message: t("invoices.toasts.created", {
            number: detail?.holdedDocNumber || "",
          }),
          severity: "success",
        })
      );
      if (detail?.id) navigate(`/invoices/${detail.id}`);
    } catch (e: any) {
      // 502 — guardada en local pero Holded falló. El envelope viaja en e.data,
      // con e.data.data = InvoiceDetail (holdedStatus: "ERROR"). NO es pérdida de datos.
      if (e?.status === 502 && e?.data?.data?.id) {
        const detail = e.data.data;
        setConfirmOpen(false);
        dispatch(
          setToastMessage({
            message: t("invoices.toasts.createdNotSent"),
            severity: "warning",
            autoHideDuration: 6000,
          })
        );
        navigate(`/invoices/${detail.id}`);
        return;
      }
      // 400 (validación, incl. empresa sin holdedContactId) y demás errores.
      // Cerramos el modal para que el usuario vuelva al formulario y corrija.
      setConfirmOpen(false);
      dispatch(
        setToastMessage({
          message: e?.data?.message || t("invoices.toasts.saveError"),
          severity: "error",
        })
      );
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "80px" : "250px",
        }}
      >
        <PageBreadcrumbs />

        <PageContainer>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h3" gutterBottom>
                {t("invoices.title")}
              </Typography>
              <Typography
                variant="pSmallest"
                color={theme.palette.neutral["600"]}
              >
                {t("invoices.subtitle")}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => navigate("/invoices/history")}
              sx={{ textTransform: "none" }}
            >
              {t("invoices.historyButton")}
            </Button>
          </Box>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Emisora */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: "100%" }}>
                <Typography variant="pMedium" sx={{ fontWeight: 600, mb: 2 }}>
                  {t("invoices.issuer")}
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={issuerId}
                  onChange={(e) => setIssuerId(e.target.value)}
                >
                  {issuers.map((i) => (
                    <MenuItem key={i.id} value={i.id}>
                      {i.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Paper>
            </Grid>

            {/* Cliente */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: "100%" }}>
                <Typography variant="pMedium" sx={{ fontWeight: 600, mb: 2 }}>
                  {t("invoices.client")}
                </Typography>
                <InvoiceClientSelector
                  clients={clients}
                  selectedId={billingId}
                  onPick={pickClient}
                />
                <Typography
                  variant="pSmallest"
                  color={theme.palette.neutral["600"]}
                  sx={{ mt: 1, display: "block" }}
                >
                  {t("invoices.clientHint")}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Fecha y OC: van despues del cliente porque el selector de OC solo
            ofrece las de ese cliente, y vacio sin cliente no se entiende. */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={4}>
                <Typography variant="pSmall" sx={fieldLabelSx}>
                  {t("invoices.date")}
                </Typography>
                <TextField
                  type="date"
                  fullWidth
                  size="small"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography variant="pSmall" sx={fieldLabelSx}>
                  {t("invoices.ocOrder")} *
                </Typography>
                <OcOrderSelector
                  orders={ocOrders}
                  selectedId={ocOrderId}
                  onPick={pickOcOrder}
                  disabled={!selectedClient}
                />
                {!selectedClient && (
                  <Typography
                    variant="pSmallest"
                    color={theme.palette.neutral["600"]}
                    sx={{ mt: 1, display: "block" }}
                  >
                    {t("invoices.ocOrderHint")}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>

          {/* Líneas */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <InvoiceItemsTable
              items={items}
              onUpdate={updateItem}
              onAdd={addItem}
              onDelete={deleteItem}
              onAddFee={handleAddFeeClick}
              addFeeDisabled={!selectedClient}
            />

            <Menu
              anchorEl={feeMenuAnchor}
              open={Boolean(feeMenuAnchor)}
              onClose={() => setFeeMenuAnchor(null)}
            >
              <MenuItem onClick={() => pickFee("variable")}>
                {t("invoices.feeMenu.variable", {
                  pct: selectedClient?.feeVariable ?? 0,
                })}
              </MenuItem>
              <MenuItem onClick={() => pickFee("fixed")}>
                {t("invoices.feeMenu.fixed", {
                  amount: formatEUR(selectedClient?.feeFixed ?? 0),
                })}
              </MenuItem>
            </Menu>

            {/* Total (solo display; el backend es la fuente de verdad) */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  minWidth: 280,
                  bgcolor: theme.palette.neutral["100"],
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 0.75,
                  }}
                >
                  <Typography variant="pSmall" sx={{ fontWeight: 700 }}>
                    {t("invoices.totals.total")}
                  </Typography>
                  <Typography variant="pSmall" sx={{ fontWeight: 700 }}>
                    {formatEUR(total)}
                  </Typography>
                </Box>
              </Paper>
            </Box>

            {/* Notas de factura (opcional). Se envía como `concept` en el POST. */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="pSmall" sx={fieldLabelSx}>
                {t("invoices.notes")}
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                value={concept}
                onChange={(e) => setConcept(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Box>
          </Paper>

          {/* Guardar */}
          <Paper sx={{ p: 2 }}>
            <Button
              variant="contained"
              fullWidth
              disabled={isSaving || !ocOrderId}
              onClick={handleOpenConfirm}
              sx={{ textTransform: "none", py: 1.2 }}
            >
              {t("invoices.saveButton")}
            </Button>
          </Paper>
        </PageContainer>
      </Box>

      <InvoiceConfirmDialog
        open={confirmOpen}
        isLoading={isSaving}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirmSend}
        summary={{
          invoiceDate,
          clientLabel: selectedClient
            ? `${selectedClient.fiscalName} · ${selectedClient.companyLabel}`
            : "",
          issuerLabel: selectedIssuer?.label ?? "",
          ocNumber: selectedOcOrder?.ocNumber ?? "",
          items,
          total,
          notes: concept.trim() || undefined,
        }}
      />
    </Box>
  );
};

export default Invoices;
